import { calculateDcfValuation, type DcfValuationInput } from "./calculate";

export interface SensitivityCell {
  wacc: number;
  growthRate: number;
  enterpriseValue: number;
  equityValue: number;
}

export interface SensitivityResult {
  baseCase: SensitivityCell;
  matrix: SensitivityCell[][];
  waccSteps: number[];
  growthSteps: number[];
}

/**
 * Runs a sensitivity analysis over a grid of WACC and terminal growth rate variations.
 *
 * @param input  Base DCF input (the center of the grid)
 * @param waccDeltas  Offsets to add to the base WACC, e.g. [-0.02, -0.01, 0, 0.01, 0.02]
 * @param growthDeltas  Offsets to add to the base long-term growth rate
 */
export function sensitivityAnalysis(
  input: DcfValuationInput,
  waccDeltas: number[] = [-0.02, -0.01, 0, 0.01, 0.02],
  growthDeltas: number[] = [-0.02, -0.01, 0, 0.01, 0.02],
): SensitivityResult {
  const baseResult = calculateDcfValuation(input);
  const baseWacc = baseResult.wacc.wacc;
  const baseGrowth = input.longTermGrowthRate ?? 0.035;

  const waccSteps = waccDeltas.map((d) => baseWacc + d);
  const growthSteps = growthDeltas.map((d) => baseGrowth + d);

  const matrix: SensitivityCell[][] = [];

  for (const waccOffset of waccDeltas) {
    const row: SensitivityCell[] = [];
    for (const growthOffset of growthDeltas) {
      const wacc = baseWacc + waccOffset;
      const growth = baseGrowth + growthOffset;

      if (wacc <= growth || wacc <= 0) {
        row.push({
          wacc,
          growthRate: growth,
          enterpriseValue: NaN,
          equityValue: NaN,
        });
        continue;
      }

      const result = calculateDcfValuation({
        ...input,
        longTermGrowthRate: growth,
        _waccOverride: wacc,
      });

      row.push({
        wacc,
        growthRate: growth,
        enterpriseValue: result.enterpriseValue,
        equityValue: result.equityValue,
      });
    }
    matrix.push(row);
  }

  const baseCase: SensitivityCell = {
    wacc: baseWacc,
    growthRate: baseGrowth,
    enterpriseValue: baseResult.enterpriseValue,
    equityValue: baseResult.equityValue,
  };

  return { baseCase, matrix, waccSteps, growthSteps };
}
